import { useState, useEffect, useContext } from 'react';
import { AuthContext } from '@/App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import { BarChart3, TrendingUp, DollarSign, Users, Activity } from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#FF6B6B', '#4ECDC4', '#FFB347', '#7C83DB', '#F06292', '#81C784', '#64B5F6', '#BA68C8', '#FFD54F', '#A1887F'];

const AdminAnalytics = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/dashboard'); return; }
    Promise.all([
      axios.get(`${API}/admin/analytics`, { headers }),
      axios.get(`${API}/admin/stats`, { headers })
    ]).then(([a, s]) => {
      setAnalytics(a.data);
      setStats(s.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (loading) return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar /><div className="flex items-center justify-center pt-40"><Activity className="w-8 h-8 animate-spin text-coral" /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-peach-50 to-teal-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-8 pt-24">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <BarChart3 className="w-8 h-8 text-coral" />
            <h1 className="text-4xl font-bold">Analytics</h1>
          </div>
          <button onClick={() => navigate('/admin')} className="text-coral hover:underline font-medium">
            Back to Admin
          </button>
        </div>

        {/* KPI Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="kpi-cards">
            <div className="glass-dark rounded-2xl p-5">
              <div className="flex items-center space-x-2 mb-1">
                <Users className="w-5 h-5 text-blue-500" />
                <span className="text-sm text-gray-500">Total Users</span>
              </div>
              <p className="text-3xl font-bold">{stats.total_users}</p>
            </div>
            <div className="glass-dark rounded-2xl p-5">
              <div className="flex items-center space-x-2 mb-1">
                <TrendingUp className="w-5 h-5 text-yellow-500" />
                <span className="text-sm text-gray-500">Premium</span>
              </div>
              <p className="text-3xl font-bold">{stats.premium_users}</p>
              <p className="text-xs text-gray-400">{stats.total_users ? ((stats.premium_users / stats.total_users) * 100).toFixed(1) : 0}% conversion</p>
            </div>
            <div className="glass-dark rounded-2xl p-5">
              <div className="flex items-center space-x-2 mb-1">
                <DollarSign className="w-5 h-5 text-green-500" />
                <span className="text-sm text-gray-500">Revenue</span>
              </div>
              <p className="text-3xl font-bold">${analytics?.total_revenue?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="glass-dark rounded-2xl p-5">
              <div className="flex items-center space-x-2 mb-1">
                <Activity className="w-5 h-5 text-coral" />
                <span className="text-sm text-gray-500">Events</span>
              </div>
              <p className="text-3xl font-bold">{stats.total_events}</p>
            </div>
          </div>
        )}

        {analytics && (
          <div className="space-y-8">
            {/* User Growth Chart */}
            <div className="glass-dark rounded-2xl p-6" data-testid="user-growth-chart">
              <h3 className="text-lg font-bold mb-4">User Growth (30 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={analytics.user_growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} interval={4} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="users" stroke="#FF6B6B" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Daily Signups */}
              <div className="glass-dark rounded-2xl p-6" data-testid="signups-chart">
                <h3 className="text-lg font-bold mb-4">Daily Signups (14 Days)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.signups}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="signups" fill="#4ECDC4" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Subscription Breakdown */}
              <div className="glass-dark rounded-2xl p-6" data-testid="subscription-chart">
                <h3 className="text-lg font-bold mb-4">Subscription Breakdown</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie data={analytics.subscription_breakdown} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                      {analytics.subscription_breakdown.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Messages Per Day */}
              <div className="glass-dark rounded-2xl p-6" data-testid="messages-chart">
                <h3 className="text-lg font-bold mb-4">Messages Per Day (14 Days)</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={analytics.messages_per_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="messages" fill="#FFB347" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Event Participation */}
              <div className="glass-dark rounded-2xl p-6" data-testid="events-chart">
                <h3 className="text-lg font-bold mb-4">Event Participation</h3>
                {analytics.event_participation.length > 0 ? (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={analytics.event_participation} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={120} />
                      <Tooltip />
                      <Bar dataKey="attendees" fill="#7C83DB" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-400 text-center py-16">No events yet</p>
                )}
              </div>
            </div>

            {/* Top Interests Table */}
            <div className="glass-dark rounded-2xl p-6" data-testid="interests-table">
              <h3 className="text-lg font-bold mb-4">Top Interests</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {analytics.top_interests.map((item, i) => (
                  <div key={i} className="rounded-xl p-3 text-center" style={{ backgroundColor: `${COLORS[i % COLORS.length]}15` }}>
                    <p className="font-semibold text-sm">{item.interest}</p>
                    <p className="text-2xl font-bold mt-1" style={{ color: COLORS[i % COLORS.length] }}>{item.count}</p>
                    <p className="text-xs text-gray-400">users</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
